import React, { useState, useEffect, useCallback } from 'react';
import { fetchRuns } from './onboardingApi';
import RunPanel from './RunPanel';

/**
 * All onboarding runs across every institution — including runs for slugs
 * that have no `institutions` row yet (pre-S2). The institution selector can
 * only reach seeded institutions, so without this section a fresh run (e.g.
 * `ucmerced` before its bootstrap load) is invisible in the console.
 *
 * `currentSlug` is the institution currently shown above (its RunPanel is
 * already rendered by Onboarding.js); rows for it are labeled instead of
 * getting a duplicate panel.
 */
function PipelineRuns({ currentSlug = null }) {
  const [runs, setRuns] = useState([]);
  // Open by RUN ID, not slug — a slug can have superseded historical runs, and
  // the panel for row #49 must show run #49, not the slug's latest.
  const [openRun, setOpenRun] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRuns(await fetchRuns());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-gray-400 text-sm">Loading pipeline runs…</div>;

  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Pipeline runs — all institutions</h3>
          <p className="text-xs text-gray-400">
            Every onboarding run in the manifest, including institutions not yet
            seeded (pre-S2), which the selector above cannot reach.
          </p>
        </div>
        <button
          onClick={load}
          className="px-2 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
        >
          Refresh
        </button>
      </div>
      {error && <div className="text-red-600 text-xs mb-2">{error}</div>}
      {runs.length === 0 && !error && (
        <div className="text-sm text-gray-400">No onboarding runs recorded yet.</div>
      )}
      {runs.map((r) => {
        const isCurrent = currentSlug !== null && r.institution_slug === currentSlug;
        const isOpen = openRun !== null && openRun.id === r.id;
        return (
          <div
            key={r.id}
            className="flex items-center gap-3 text-sm py-1.5 border-b border-gray-50 last:border-0"
          >
            <span className="w-10 font-mono text-xs text-gray-400">#{r.id}</span>
            <span className="font-mono text-gray-800">{r.institution_slug}</span>
            {/* A slug can now have BOTH an onboarding and a monitoring run
                active at once (migration 092) — without this badge the two
                rows are indistinguishable. Onboarding is the default and
                stays unlabelled so existing rows read exactly as before. */}
            {r.purpose === 'monitoring' && (
              <span className="px-1.5 py-0.5 text-[10px] uppercase tracking-wide bg-blue-50 text-blue-700 rounded">
                monitoring
              </span>
            )}
            <span className="text-xs text-gray-500">{r.status}</span>
            <span className="flex-1 text-xs text-gray-400">
              {r.decisions ? `decisions rev ${r.decisions.revision}` : 'no decisions yet'}
              {' · '}created by <span className="font-mono">{r.created_by}</span>
              {' · '}{r.created_at.slice(0, 10)}
            </span>
            {isCurrent ? (
              <span className="text-xs text-gray-400">(shown above)</span>
            ) : (
              <button
                onClick={() => setOpenRun(isOpen ? null : r)}
                className="px-2 py-0.5 text-xs bg-gray-100 rounded hover:bg-gray-200"
              >
                {isOpen ? 'Close' : 'Open'}
              </button>
            )}
          </div>
        );
      })}
      {openRun && (
        <div className="mt-3">
          <RunPanel slug={openRun.institution_slug} runId={openRun.id} />
        </div>
      )}
    </div>
  );
}

export default PipelineRuns;
